import { CategoryFieldType, CategoryFieldTypeEnum, PaginationType, PostType } from 'types';
import { __PostFormDataType, __PostFormType } from './addPost.type';
import { AppStorage, RestAPI } from 'scripts';
import { API } from 'data';
import { message } from 'antd';

const __ConvertDataForPost = (e: __PostFormType) => {
    return {
        title: e.title,
        category: e.category,
        description: e.description,
        has_phone: e.has_phone,
        locations: e.locations.map((i) => i.id),
        latitude: e.latitude,
        longitude: e.longitude,
        chatable: e.chatable,
        gallery: e.gallery?.map((i) => i.id),
        socials: e.socials ? JSON.stringify(e.socials) : null,
    };
};

export const __ConvertAPIDataToForm = (e?: PostType<'detail'>) => {
    const categoryFieldsValues: __PostFormType['category_fields_values'] = {};
    e?.category_fields_values?.map((item) => {
        const type = item.category_field.type;
        if (type === CategoryFieldTypeEnum.text && item.value !== undefined && item.value !== null)
            categoryFieldsValues[item.category_field.id] = item.value;
        else if (type === CategoryFieldTypeEnum.number && item.value_number !== undefined && item.value_number !== null)
            categoryFieldsValues[item.category_field.id] = Number(item.value_number);
        else if (type === CategoryFieldTypeEnum.boolean && item.value !== undefined && item.value !== null)
            categoryFieldsValues[item.category_field.id] = item.value;
        else if (type === CategoryFieldTypeEnum.fieldOptions && item.option)
            categoryFieldsValues[item.category_field.id] = item.option.id;
    });
    if (e)
        return {
            title: e.title,
            description: e.description,
            latitude: e.latitude,
            longitude: e.longitude,
            chatable: e.chatable,
            has_phone: e.has_phone,
            gallery: e.gallery,
            socials: e.socials ? e.socials : undefined,
            category: e.category.id.toString(),
            locations: e.locations?.map((i) => i.id.toString()),
            category_fields_values: categoryFieldsValues,
        };
};

const __ConvertCFVForAPI = (e: __PostFormType, postSlug: string, categoryFields?: CategoryFieldType<'detail'>[]) => {
    return {
        post: postSlug,
        field_values: e.category_fields_values
            ? Object.entries(e.category_fields_values)
                  .filter((item) => item[1] !== undefined && item[1] !== null)
                  .map((item) => {
                      const obj: { value?: string; value_number?: number; option?: number; category_field: number } = {
                          category_field: Number(item[0]),
                      };
                      const type = categoryFields?.find((i) => i.id === Number(item[0]))?.type;
                      if (type === CategoryFieldTypeEnum.text) obj.value = item[1].toString();
                      else if (type === CategoryFieldTypeEnum.number) obj.value_number = Number(item[1]);
                      else if (type === CategoryFieldTypeEnum.boolean) obj.value = item[1] === 1 ? '1' : '0';
                      else if (type === CategoryFieldTypeEnum.fieldOptions) obj.option = Number(item[1]);
                      return obj;
                  })
            : null,
    };
};

export const __CalcPostPayment = (formData: __PostFormType, data?: __PostFormDataType) => {
    let locCof = 0;
    formData.locations.map((l) => {
        const loc = data?.locations.find((i) => i.id === l.id);
        if (loc) locCof = locCof + (loc.coefficient ?? 0);
    });
    const cat = data?.categories.find((i) => i.id.toString() === formData.category);
    if (cat) return (cat.price ?? 0) * locCof;
    else return 0;
};

export const __PostFormAPICall = (
    formData: __PostFormType,
    slug?: string,
    categoryFields?: CategoryFieldType<'detail'>[],
    hideSuccessMessage?: boolean,
) => {
    return new Promise<PostType<'detail'>>((resolve, reject) => {
        const request = slug ? RestAPI.put : RestAPI.post;
        const url = slug ? API.posts.update(slug) : API.posts.create;
        request<PostType<'detail'>>(url, __ConvertDataForPost(formData))
            .then((res) => {
                __UpdateLastPostLocation(formData);
                RestAPI.post(
                    API.categoryField.createBulkValues,
                    __ConvertCFVForAPI(formData, res.data.slug, categoryFields),
                )
                    .then(() => {
                        if (!hideSuccessMessage)
                            message.success(
                                `${slug ? 'ویرایش' : 'ساخت'} آگهی با موفقیت انجام شد و پس از تایید مدیریت در لیست آگهی های تیک سبز قرار خواهد گرفت.`,
                            );
                        resolve(res.data);
                    })
                    .catch(() => {
                        message.error(`فرآیند ${slug ? 'ویرایش' : 'ساخت'} آگهی با خطا مواجه شد. لطفا بعدا تلاش کنید.`);
                        reject();
                    });
            })
            .catch(() => {
                message.error(`فرآیند ${slug ? 'ویرایش' : 'ساخت'} آگهی با خطا مواجه شد. لطفا بعدا تلاش کنید.`);
                reject();
            });
    });
};

export const __GetInitialPostFormData = (initialData: __PostFormDataType, slug?: string) => {
    return new Promise<{ post: PostType<'detail'>; data: __PostFormDataType }>((resolve) => {
        if (slug) {
            RestAPI.get<PostType<'detail'>>(API.posts.myPostDetail(slug)).then((res) => {
                RestAPI.get<PaginationType<CategoryFieldType<'detail'>>>(API.categoryField.list, {
                    category_ancestors: res.data.category.id,
                    page_size: 25,
                })
                    .then((innerRes) =>
                        resolve({
                            post: res.data,
                            data: {
                                categoryFields: innerRes.data.data,
                                categories: initialData.categories.find((i) => i.id === res.data.category.id)
                                    ? initialData.categories
                                    : initialData.categories.concat(res.data.category),
                                locations: initialData.locations.concat(
                                    res.data.locations?.filter(
                                        (i) => !initialData.locations.map((i) => i.id).includes(i.id),
                                    ) ?? [],
                                ),
                            },
                        }),
                    )
                    .catch(() =>
                        resolve({
                            post: res.data,
                            data: {
                                categories: initialData.categories.find((i) => i.id === res.data.category.id)
                                    ? initialData.categories
                                    : initialData.categories.concat(res.data.category),
                                locations: initialData.locations.concat(
                                    res.data.locations?.filter(
                                        (i) => !initialData.locations.map((i) => i.id).includes(i.id),
                                    ) ?? [],
                                ),
                            },
                        }),
                    );
            });
        }
    });
};

export const __UpdateLastPostLocation = (e: __PostFormType) => {
    AppStorage.lastPostLocation.set({
        location: e.locations.length ? e.locations[0] : undefined,
        latitude: e.latitude,
        longitude: e.longitude,
        address: e.address,
    });
};
